import * as React from 'react';
import * as SeparatorPrimitive from '@radix-ui/react-separator';

import { Text } from '@/hen-ui/atomics/Text';
import { COMPONENT_IDS } from '@/hen-ui/catalog/component-ids';
import { Flex } from '@/hen-ui/composites/Flex';
import { Separator as ShadcnSeparator } from '@/hen-ui/shadcn/separator';
import {
  separatorClassName,
  separatorLabeledRuleClassName,
  type SeparatorFill,
  type SeparatorRadius,
  type SeparatorThickness,
  type SeparatorTone,
} from './separator.variants';

export type SeparatorProps = React.ComponentPropsWithoutRef<
  typeof SeparatorPrimitive.Root
> & {
  /** How thick the line is. Omit for the usual thin line. */
  thickness?: SeparatorThickness;
  /** How rounded the corners are. Omit for square corners. */
  radius?: SeparatorRadius;
  /** Solid divider, or the tenant leading-rule gradient painted over it. */
  fill?: SeparatorFill | '';
  /** How the line reads on the surface behind it. Omit for the usual divider. */
  tone?: SeparatorTone | '';
  /** Words centered on a horizontal line. Omit or leave blank for a plain line. */
  label?: string;
};

function hasSeparatorLabel(label: string | undefined): label is string {
  return (label?.trim().length ?? 0) > 0;
}

function SeparatorRule({
  thickness,
  radius,
  fill,
  tone,
}: {
  thickness?: SeparatorThickness;
  radius?: SeparatorRadius;
  fill?: SeparatorFill | '';
  tone?: SeparatorTone | '';
}) {
  return (
    <div
      aria-hidden
      className={separatorLabeledRuleClassName({
        thickness,
        radius,
        fill,
        tone,
      })}
    />
  );
}

export const Separator = React.forwardRef<
  React.ElementRef<typeof SeparatorPrimitive.Root>,
  SeparatorProps
>(
  (
    {
      className,
      decorative = true,
      orientation = 'horizontal',
      thickness,
      radius,
      fill,
      tone,
      asChild,
      label,
      ...otherProps
    },
    ref,
  ) => {
    if (orientation !== 'vertical' && hasSeparatorLabel(label)) {
      const { children, ...domProps } = otherProps;
      const rulesAndLabel = (
        <>
          <SeparatorRule
            thickness={thickness}
            radius={radius}
            fill={fill}
            tone={tone}
          />
          <Text
            tag='span'
            variant='b4'
            color={tone === 'inverse' ? 'white' : 'subtext'}
          >
            {label}
          </Text>
          <SeparatorRule
            thickness={thickness}
            radius={radius}
            fill={fill}
            tone={tone}
          />
        </>
      );
      // Slot can merge onto the supplied element only. The rules and label
      // render inside that element so its props and ref stay on the root.
      const useChildRoot = Boolean(asChild) && React.isValidElement(children);
      const host = useChildRoot
        ? React.cloneElement(children, undefined, rulesAndLabel)
        : rulesAndLabel;

      return (
        <Flex
          asChild={useChildRoot}
          ref={ref}
          direction='row'
          align='center'
          gap='xs'
          width='full'
          className={className}
          {...domProps}
          data-hen-id={COMPONENT_IDS.Separator}
        >
          {host}
        </Flex>
      );
    }

    return (
      <ShadcnSeparator
        ref={ref}
        asChild={asChild}
        data-hen-id={COMPONENT_IDS.Separator}
        className={separatorClassName({
          orientation,
          thickness,
          radius,
          fill,
          tone,
          className,
        })}
        decorative={decorative}
        orientation={orientation}
        role='separator'
        {...otherProps}
      />
    );
  },
);

Separator.displayName = 'Separator';
